<template>
  <div class="base-output">
    <div v-if="!isEditing" class="display-mode" @click="startEditing">
      <span class="output-text" :class="{ 'not-set': !displayValue }">
        {{ displayValue || emptyText }}
      </span>
      <BaseIcon
        v-if="editable"
        :iconName="editIcon"
        class="edit-icon"
        :color="editIconColor"
        :width="editIconSize"
        :height="editIconSize"
      />
    </div>

    <div v-else class="edit-mode">
      <input
        ref="inputRef"
        type="text"
        v-model="tempValue"
        @blur="handleBlur"
        @keyup.enter="saveValue"
        @keyup.esc="cancelEdit"
        :placeholder="placeholder || modelValue"
        class="edit-input"
      />
      <div v-if="showActions" class="edit-actions">
        <button class="action-btn save" @click="saveValue" type="button">
          <BaseIcon iconName="check" :width="20" :height="20" />
        </button>
        <button class="action-btn cancel" @click="cancelEdit" type="button">
          <BaseIcon iconName="close" :width="20" :height="20" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, computed } from 'vue';
import BaseIcon from '@/components/BaseComponents/BaseIcon.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  editable: {
    type: Boolean,
    default: true
  },
  placeholder: {
    type: String,
    default: ''
  },
  emptyText: {
    type: String,
    default: 'Not set'
  },
  maxLength: {
    type: Number,
    default: null
  },
  showActions: {
    type: Boolean,
    default: true
  },
  editIcon: {
    type: String,
    default: 'pencil'
  },
  editIconColor: {
    type: String,
    default: 'rgba(255, 255, 255, 0.4)'
  },
  editIconSize: {
    type: Number,
    default: 16
  }
});

const emit = defineEmits(['update:modelValue', 'save']);

const isEditing = ref(false);
const tempValue = ref('');
const inputRef = ref(null);

const displayValue = computed(() => {
  if (!props.modelValue) return '';

  if (props.maxLength && props.modelValue.length > props.maxLength) {
    return props.modelValue.slice(0, props.maxLength) + '...';
  }

  return props.modelValue;
});

function startEditing() {
  if (!props.editable) return;

  tempValue.value = props.modelValue;
  isEditing.value = true;
  nextTick(() => {
    inputRef.value?.focus();
  });
}

function cancelEdit() {
  isEditing.value = false;
  tempValue.value = props.modelValue;
}

function handleBlur(event) {
  if (props.showActions && event.relatedTarget?.classList.contains('action-btn')) {
    return;
  }

  if (!props.showActions) {
    saveValue();
  } else {
    cancelEdit();
  }
}

function saveValue() {
  const trimmedValue = tempValue.value.trim();

  if (trimmedValue !== props.modelValue) {
    emit('update:modelValue', trimmedValue);
    emit('save', trimmedValue);
  }

  isEditing.value = false;
}
</script>

<style scoped>
.base-output {
  width: 100%;
  min-height: 32px;
}

.display-mode {
  display: flex;
  align-items: center;
  gap: var(--gap-lg);
  min-height: 32px;
  cursor: pointer;
  padding: var(--spacing-sm) 0;
}

.display-mode:not(:has(.edit-icon)) {
  cursor: default;
}

.output-text {
  color: var(--color-white);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-body-alt);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.output-text.not-set {
  color: var(--color-white-40);
  font-style: italic;
}

.edit-icon {
  transition: var(--transition-fast);
  display: flex;
  align-items: center;
  flex-shrink: 0;
  opacity: 0.6;
}

.display-mode:hover .edit-icon {
  opacity: 1;
}

.edit-mode {
  display: flex;
  gap: var(--gap-lg);
  align-items: center;
  min-height: 32px;
}

.edit-input {
  flex: 1;
  background: transparent;
  border: var(--border-width-thin) solid var(--color-white-30);
  border-radius: var(--border-radius-xs);
  padding: var(--spacing-sm) var(--spacing-lg);
  color: var(--color-white);
  font-size: inherit;
  min-height: 32px;
  line-height: var(--line-height-body-alt);
}

.edit-input:focus {
  outline: none;
  border-color: var(--color-accent-primary);
  background: var(--color-white-05);
}

.edit-input::placeholder {
  color: var(--color-white-40);
}

.edit-actions {
  display: flex;
  gap: var(--gap-sm);
  height: 32px;
  flex-shrink: 0;
}

.action-btn {
  background: var(--color-white-05);
  border: none;
  padding: var(--spacing-sm);
  cursor: pointer;
  border-radius: var(--border-radius-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  transition: var(--transition-fast);
}

.action-btn.save {
  color: var(--color-accent-primary);
}

.action-btn.save:hover {
  background: var(--color-accent-primary-15);
}

.action-btn.cancel {
  color: var(--color-cancel);
}

.action-btn.cancel:hover {
  background: var(--color-cancel-15);
}
</style>
